import { useState } from 'react';
import { Button, Table, TableBody, TableCell, TableHead, TablePagination, TableRow, Tooltip, useMediaQuery } from '@mui/material';
import GetAppIcon from '@mui/icons-material/GetApp';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { useDownloadApplicationFileStore } from '@lib';
import { FileEntity } from '@shared/api';
import { IconButton } from '@widgets';

interface Props {
  data: FileEntity[];
  deleteFile?: (fileId: string) => void;
  onRejectFile?: (fileId: string, fileName?: string) => void;
  readOnly?: boolean;
  showStatus?: boolean;
  deleteLoading?: boolean;
}

export const FileList = (props: Props) => {
  const { downloadApplicationFiles } = useDownloadApplicationFileStore();

  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);

  const largeScreen = useMediaQuery('(min-width:800px)');

  const canRemove = !!props.deleteFile && !props.readOnly;
  const hasStatus = !!props.onRejectFile || props.showStatus;
  const canReject = !!props.onRejectFile && !props.readOnly;

  const handleRemoveFile = (id: string) => props.deleteFile?.(id);
  const handleRejectFile = (id: string, fileName?: string) => props.onRejectFile?.(id, fileName);

  return (
    <>
      {largeScreen ? (
        <Table aria-label='simple table' stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell style={{ width: 100 }} align='left'>
                File
              </TableCell>
              <TableCell style={{ width: 100 }} align='left'>
                Type
              </TableCell>
              <TableCell style={{ width: 100 }} align='center'>
                Date Uploaded
              </TableCell>
              {hasStatus ? (
                <TableCell style={{ width: 100 }} align='center'>
                  Status
                </TableCell>
              ) : null}
              <TableCell style={{ width: 100 }} align='center'>
                Download
              </TableCell>

              {canRemove && (
                <TableCell style={{ width: 100 }} align='center'>
                  Remove
                </TableCell>
              )}
              {canReject && (
                <TableCell style={{ width: 100 }} align='center'>
                  Reject
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {props.data.map((row, idx) => (
              <TableRow key={idx}>
                <TableCell>
                  <Button color='primary' variant='text' onClick={() => downloadApplicationFiles({ fileId: row.id, filename: row.fileName })}>
                    {row.fileName}
                  </Button>
                </TableCell>
                <TableCell>{row.type}</TableCell>
                <TableCell align='center'>{new Date(row.dateUploaded).toDateString()}</TableCell>
                {hasStatus && (
                  <TableCell align='center'>
                    <Tooltip title={row.rejectReason || ''}>
                      <span>{row.status}</span>
                    </Tooltip>
                  </TableCell>
                )}
                <TableCell align='center'>
                  <IconButton onClick={() => downloadApplicationFiles({ fileId: row.id, filename: row.fileName })} size='large'>
                    <GetAppIcon />
                  </IconButton>
                </TableCell>
                {canRemove && (
                  <TableCell align='center'>
                    <IconButton onClick={() => handleRemoveFile(row.id)} loading={props.deleteLoading} size='large'>
                      <HighlightOffIcon />
                    </IconButton>
                  </TableCell>
                )}
                {canReject && props.onRejectFile ? (
                  <TableCell align='center'>
                    {row.status !== 'Rejected' ? (
                      <IconButton onClick={() => handleRejectFile(row.id, row.fileName)} size='large'>
                        <HighlightOffIcon />
                      </IconButton>
                    ) : null}
                  </TableCell>
                ) : null}
              </TableRow>
            ))}
            {!props.data.length ? (
              <TableRow>
                <TableCell colSpan={4} align='center'>
                  No Records Found
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      ) : (
        <Table aria-label='simple table' stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell style={{ width: 100 }} align='center'>
                File Id
              </TableCell>
              <TableCell style={{ width: 100 }} align='center'>
                Application Id
              </TableCell>
              <TableCell style={{ width: 100 }} align='center'>
                File Type
              </TableCell>
              <TableCell style={{ width: 100 }} align='center'>
                Date Uploaded
              </TableCell>
              <TableCell style={{ width: 100 }} align='center'>
                <GetAppIcon />
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {props.data.map((row, idx) => (
              <TableRow key={idx}>
                <TableCell align='center'>{row.id}</TableCell>
                <TableCell align='center'>{row.applicationId}</TableCell>
                <TableCell align='center'>{row.type}</TableCell>
                <TableCell align='center'>{new Date(row.dateUploaded).toDateString()}</TableCell>

                <TableCell align='center'>Download</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component='div'
        count={props.data.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(_, value) => setPage(value)}
        onRowsPerPageChange={(e) => {
          setPage(0);
          setRowsPerPage(+e.target.value);
        }}
      />
    </>
  );
};
